/**
 * Anemoia floating collection (custom).
 *
 * Lays the collection's products out as objects floating in the open space of
 * the world layer (drakerelated.com/rooms/closet-style): scattered across the
 * viewport at varied depths, drifting gently, and shifting with the cursor for
 * a parallax/3D-room feel. A white pointer follows the cursor and swells over a
 * product; hovering reveals its detail + Add to cart, clicking opens the PDP.
 *
 * Add to cart adds via the Cart AJAX API, flies the product image to the nav
 * cart icon, and dispatches the theme's CartAddEvent so the bubble + drawer
 * update natively — without leaving the room.
 */

import { CartAddEvent, ThemeEvents } from '@theme/events';
import '@theme/fly-to-cart';

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

/**
 * Pops the theme cart drawer open after an add. No-op when the store uses a cart
 * page (no drawer) or when the drawer is already open (avoids showModal throwing).
 */
function openCartDrawer() {
  const drawer = document.querySelector('cart-drawer-component');
  if (!drawer || typeof drawer.open !== 'function') return;
  if (drawer.querySelector('dialog')?.open) return;
  drawer.open();
}

/** Longest to hold the drawer for the tote's catch, if the bag update stalls. */
const BAG_CATCH_MAX_MS = 2500;
const BAG_ITEM_WIDTH = 360;

/**
 * Small deterministic PRNG (mulberry32) so each product keeps the same scatter
 * position, depth and float timing across re-layouts and reloads.
 * @param {number} seed
 */
function makeRandom(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

class AnemoiaCollectionFloat extends HTMLElement {
  connectedCallback() {
    // The tote works even when the filter leaves the room empty.
    this.setupBag();

    this.stage = this.querySelector('[data-afloat-stage]');
    this.items = Array.from(this.querySelectorAll('[data-afloat-item]'));
    if (this.items.length === 0) return;


    // Stable per-item seed values (scatter jitter, depth, float timing).
    this.seeds = this.items.map((_, i) => {
      const rand = makeRandom((i + 1) * 2654435761);
      return { jx: rand(), jy: rand(), depth: rand(), dur: rand(), delay: rand() };
    });

    // Add to cart + category filter work in both the room and the mobile grid.
    this.setupAddToCart();
    this.setupMotes();
    this.setupGoat();
    this.setupFilter();

    // Touch (coarse pointer) gets tap-to-focus instead of hover-to-focus.
    this.coarse = !window.matchMedia('(pointer: fine)').matches;

    // The horizontal room (auto-drift + bob + swipe) now runs everywhere. On
    // desktop it also adds mouse parallax + the white cursor (both guarded to
    // fine pointers); on touch it's a 2-up swipe track (see layout()).
    this.setupScroll();
    this.layout();
    this.onResize = () => this.layout();
    window.addEventListener('resize', this.onResize, { passive: true });
    // The heading grows when the display fonts arrive; re-measure the duck-under
    // zone (and item heights) once they're in.
    document.fonts?.ready?.then(() => this.layout());
    // SOLAR/LUNAR switch swaps the baked-clothesline video in and out —
    // re-measure so the products hop between the wire line and the flat row.
    this.onModeChange = () => this.layout();
    document.addEventListener('anemoia:mode-change', this.onModeChange);

    this.setupCursor();
    this.startMotion();

    this.classList.add('is-ready');
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onResize);
    if (this.onModeChange) document.removeEventListener('anemoia:mode-change', this.onModeChange);
    if (this.onPointerMove) {
      this.removeEventListener('pointermove', this.onPointerMove);
      this.removeEventListener('pointerenter', this.onPointerMove);
    }
    if (this.onPointerLeave) this.removeEventListener('pointerleave', this.onPointerLeave);
    if (this.onClick) this.removeEventListener('click', this.onClick);
    if (this.filterEl && this.onFilter) this.filterEl.removeEventListener('change', this.onFilter);
    if (this.onWheel) this.removeEventListener('wheel', this.onWheel);
    if (this.onDragStart) this.removeEventListener('pointerdown', this.onDragStart);
    if (this.onDragMove) window.removeEventListener('pointermove', this.onDragMove);
    if (this.onDragEnd) window.removeEventListener('pointerup', this.onDragEnd);
    if (this.cursorEl) this.cursorEl.remove();
    if (this.motionRaf) cancelAnimationFrame(this.motionRaf);
    if (this.goatBlobUrl) URL.revokeObjectURL(this.goatBlobUrl);
    if (this.onBagCartUpdate) document.removeEventListener(ThemeEvents.cartUpdate, this.onBagCartUpdate);
  }

  /**
   * Single render loop that drives all horizontal motion. Each frame it (a) lets
   * the ambient auto-slide nudge `this.target` back and forth (desktop, paused
   * on interaction/hover/open picker/hidden tab) and (b) eases the rendered
   * `this.scroll` toward `this.target` with a frame-rate-independent factor, so
   * both wheel flicks and the auto-drift read as one smooth glide — no CSS
   * transition, no jerk.
   */
  startMotion() {
    let speed = this.dataset.autoplaySpeed === undefined ? 26 : Number(this.dataset.autoplaySpeed);
    // Slow the drift a touch on mobile so it's easier to tap a passing product.
    if (window.matchMedia('(max-width: 749px)').matches) speed = 22;
    this.autoplaySpeed = !reducedMotion.matches && Number.isFinite(speed) && speed > 0 ? speed : 0;
    this.autoDir = 1;
    let last = 0;

    const render = (now) => {
      const dt = last ? Math.min(0.05, (now - last) / 1000) : 0;
      last = now;

      // (a) Ambient drift advances the target and ping-pongs at the ends.
      const blocked =
        this.drag.active || this.hovering || this.selected || this.openSizes || document.hidden;
      if (this.autoplaySpeed > 0 && !blocked && dt > 0 && this.maxScroll > 0) {
        this.target += this.autoDir * this.autoplaySpeed * dt;
        if (this.target >= this.maxScroll) {
          this.target = this.maxScroll;
          this.autoDir = -1;
        } else if (this.target <= 0) {
          this.target = 0;
          this.autoDir = 1;
        }
      }

      // (b) Ease the rendered position toward the target (skip while dragging,
      // which already tracks the finger 1:1).
      if (!this.drag.active) {
        const k = reducedMotion.matches ? 1 : dt > 0 ? 1 - Math.pow(0.0018, dt) : 0.16;
        const next = this.scroll + (this.target - this.scroll) * k;
        this.scroll = Math.abs(next - this.target) < 0.08 ? this.target : next;
        this.applyScroll();
      }

      this.motionRaf = requestAnimationFrame(render);
    };
    this.motionRaf = requestAnimationFrame(render);
  }

  /**
   * Horizontal track on one centred line: `visible` products fill the viewport
   * with equal spacing; the rest sit off to the right and are revealed by
   * scrolling / dragging sideways.
   */
  layout() {
    const vw = window.innerWidth;
    // Only the products passing the current category filter are placed.
    const items = this.items.filter((item) => !item.classList.contains('is-hidden'));
    const n = items.length;
    if (n === 0) {
      this.maxScroll = 0;
      this.applyScroll();
      return;
    }
    // Two products per screen on mobile; the configured count on wider screens.
    const wantVisible = window.matchMedia('(max-width: 749px)').matches
      ? 2
      : Number(this.dataset.visible) || 4;
    const visible = Math.min(Math.max(2, wantVisible), n);
    this.step = vw / visible;
    const trackWidth = n * this.step;
    // Centre the line when everything already fits on screen.
    const startX = trackWidth < vw ? (vw - trackWidth) / 2 : 0;

    items.forEach((item, i) => {
      const seed = this.seeds[this.items.indexOf(item)] || this.seeds[i];
      const cx = startX + i * this.step + this.step / 2;
      item.style.left = `${cx.toFixed(1)}px`;
      item.style.top = 'var(--afloat-row-top, 58%)';
      item.style.setProperty('--depth', '0.8');
      item.style.setProperty('--scale', '1');
      // Wind-sway timing/phase/amplitude, seeded per item so the hung row
      // doesn't swing in lock-step (drives afloat-sway in the CSS).
      item.style.setProperty('--float-dur', `${(3.8 + seed.dur * 2.6).toFixed(2)}s`);
      item.style.setProperty('--float-delay', `${(-seed.delay * 6).toFixed(2)}s`);
      item.style.setProperty('--sway-amp', `${(1.8 + seed.jx * 1.6).toFixed(2)}deg`);
      item.style.zIndex = '10';
      item._cx = cx; // track x on the stage, for the wire-hang math
      item._halfH = item.getBoundingClientRect().height / 2 || 0;
    });

    this.maxScroll = Math.max(0, trackWidth - vw);
    this.scroll = clamp(this.scroll || 0, 0, this.maxScroll);
    this.target = clamp(this.target || 0, 0, this.maxScroll);
    this.measureScene();
    this.applyScroll();
  }

  /**
   * Host-relative geometry of the clothesline and the heading. The rope is a
   * DOM element that hangs just below the heading's "N pieces" line so the row
   * reads as pinned right under the title. On mobile (narrow portrait) the row
   * is centred in the frame instead, since the heading sits high there. Both
   * modes hang the same way; only the rope's look differs (SOLAR = twine,
   * LUNAR = glowing blue).
   */
  measureScene() {
    const style = getComputedStyle(this);

    // How far above the rope each product's top edge sits. The clipper PNGs
    // carry different amounts of empty space above their pegs, so this is the
    // dial that lifts the whole row onto the line — and read the other way, the
    // value that lands a product on the rope is how much transparency that
    // file has above its pegs.
    const lift = parseFloat(style.getPropertyValue('--afloat-hang-lift'));
    this.hangLift = Number.isFinite(lift) ? lift : 6;

    // Heading box (host-relative). offsetParent is the position:relative host,
    // so these coordinates share the same origin as wireY.
    const head = this.querySelector('.afloat__heading');
    this.headRect = head
      ? { left: head.offsetLeft, right: head.offsetLeft + head.offsetWidth, bottom: head.offsetTop + head.offsetHeight }
      : null;

    const mobile = window.matchMedia('(max-width: 749px)').matches;

    if (mobile) {
      // On the narrow portrait screen the heading sits high, so anchoring under
      // it would ride the row up at the top. Centre the hung row in the frame
      // instead: place the wire a card-half above middle so each product's
      // centre lands on the viewport centre (updateHang hangs from wireY + halfH).
      let halfH = 0;
      for (const item of this.items) {
        if (!item.classList.contains('is-hidden')) halfH = Math.max(halfH, item._halfH || 0);
      }
      this.wireY = Math.max(this.clientHeight * 0.14, this.clientHeight / 2 - halfH + 6);
      // Nudge the whole hung row down the phone screen (setting, % of height).
      // The products hang from wireY, so shifting it moves rope and cloth together.
      const drop = parseFloat(style.getPropertyValue('--afloat-wire-drop-mobile'));
      if (Number.isFinite(drop)) this.wireY += (this.clientHeight * drop) / 100;
    } else if (this.headRect) {
      // Anchor the wire just under the heading — robust across breakpoints,
      // since the heading height is font/viewport dependent.
      const gap = parseFloat(style.getPropertyValue('--afloat-wire-gap')) || 26;
      this.wireY = this.headRect.bottom + gap;
    } else {
      // No heading (title hidden): fall back to the % position, undoing the
      // 16:9 backdrop cover-crop so the line sits where the setting expects.
      const wireTop = parseFloat(style.getPropertyValue('--afloat-wire-top'));
      if (Number.isFinite(wireTop)) {
        const renderedH = Math.max(this.clientHeight, (this.clientWidth * 9) / 16);
        const cropY = (renderedH - this.clientHeight) / 2;
        this.wireY = (renderedH * wireTop) / 100 - cropY;
      } else {
        this.wireY = null;
      }
    }

    if (this.wireY === null) this.items.forEach((item) => item.style.setProperty('--ry', '0px'));
    // Draw the clothesline rope at the same on-screen line the hangers use, so
    // the straight wire reads as what the products are pinned to (both modes;
    // the LUNAR look is restyled in CSS).
    if (!this.wireEl) this.wireEl = this.querySelector('[data-afloat-wire]');
    if (this.wireEl && Number.isFinite(this.wireY)) this.wireEl.style.top = `${this.wireY.toFixed(1)}px`;

    const rowTop = parseFloat(style.getPropertyValue('--afloat-row-top'));
    this.rowBaseY = (this.clientHeight * (Number.isFinite(rowTop) ? rowTop : 58)) / 100;
  }

  /**
   * Hang each product from the clothesline: its hanger top sits just under the
   * rope, whatever the card's height, so the row reads as clipped onto the
   * wire. Inside the heading zone the item eases down below the collection
   * title instead of riding the wire up into the text, and eases back onto the
   * wire once past it — in both directions, since the offset is a pure function
   * of x.
   */
  updateHang() {
    if (this.wireY === null || this.wireY === undefined || !this.rowBaseY) return;
    for (const item of this.items) {
      if (item.classList.contains('is-hidden') || item._cx === undefined) continue;
      const x = item._cx - this.scroll;
      let y = this.wireY + item._halfH - this.hangLift; // hanger top pokes above the line so the hook grabs the wire
      if (this.headRect) {
        const edge = this.headRect.right + 60; // start ducking a bit before the text
        const span = 180; // ease-down runway
        let w = clamp((edge + span - x) / span, 0, 1);
        w = w * w * (3 - 2 * w);
        const floorY = this.headRect.bottom + item._halfH + 10;
        y += (Math.max(y, floorY) - y) * w;
      }
      item.style.setProperty('--ry', `${(y - this.rowBaseY).toFixed(1)}px`);
    }
  }

  applyScroll() {
    if (this.stage) this.stage.style.transform = `translateX(${(-this.scroll).toFixed(1)}px)`;
    this.updateHang();
  }

  /**
   * Wheel + drag move the track sideways to reveal the rest of the products.
   * They only ever nudge `this.target`; the render loop (startMotion) eases the
   * rendered `this.scroll` toward it, so wheel flicks glide instead of jerking.
   * Dragging snaps 1:1 to the finger.
   */
  setupScroll() {
    this.scroll = 0;
    this.target = 0;
    this.dragged = false;
    this.justSelected = false;
    this.drag = { active: false, startX: 0, startScroll: 0 };

    this.onWheel = (event) => {
      if (this.maxScroll <= 0) return;
      event.preventDefault();
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      this.target = clamp(this.target + delta, 0, this.maxScroll);
    };
    this.addEventListener('wheel', this.onWheel, { passive: false });

    this.onDragStart = (event) => {
      // Touch: freeze the row the instant a finger lands on a product, rather
      // than waiting for the click. A tap always jitters a few pixels, which
      // used to register as a drag and swallow that click whole — so the first
      // tap appeared to do nothing and it took three to reach the PDP. Now tap
      // one stops the drift and reveals the detail, tap two opens the product.
      if (this.coarse && event.target instanceof Element) {
        this.justSelected = false;
        const item = event.target.closest('[data-afloat-item]');
        const onControl = event.target.closest('[data-afloat-add], [data-afloat-sizes]');
        if (item && !onControl && this.activeItem !== item) {
          this.selectItem(item);
          this.justSelected = true; // this gesture's own click must not navigate
        }
      }

      if (this.maxScroll <= 0) return;
      this.drag = { active: true, startX: event.clientX, startScroll: this.target };
      this.dragged = false;
      this.classList.add('afloat-dragging');
    };
    this.onDragMove = (event) => {
      if (!this.drag.active) return;
      const dx = event.clientX - this.drag.startX;
      // A finger is far less precise than a mouse: below the touch slop this is
      // still a tap, not a swipe, and must keep its click.
      const slop = this.coarse ? 12 : 5;
      if (Math.abs(dx) > slop && !this.dragged) {
        this.dragged = true;
        this.clearSelection(); // a real swipe drops the current focus
      }
      this.target = clamp(this.drag.startScroll - dx, 0, this.maxScroll);
      this.scroll = this.target; // follow the finger directly while dragging
      this.applyScroll();
    };
    this.onDragEnd = () => {
      this.drag.active = false;
      this.classList.remove('afloat-dragging');
    };
    this.addEventListener('pointerdown', this.onDragStart);
    window.addEventListener('pointermove', this.onDragMove, { passive: true });
    window.addEventListener('pointerup', this.onDragEnd);
  }

  /** White ring that follows the cursor and swells over a product. */
  setupCursor() {
    if (!window.matchMedia('(pointer: fine)').matches) return;

    this.cursorEl = document.createElement('div');
    this.cursorEl.className = 'afloat__cursor';
    // Use the custom image pointer (adampointer.png) when set; otherwise the
    // white ring. Either way the swell-on-hover animation is unchanged.
    if (this.dataset.pointer) this.cursorEl.classList.add('afloat__cursor--img');
    this.cursorEl.setAttribute('aria-hidden', 'true');
    this.appendChild(this.cursorEl);

    this.onPointerMove = (event) => {
      this.cursorEl.style.left = `${event.clientX}px`;
      this.cursorEl.style.top = `${event.clientY}px`;
      this.cursorEl.classList.add('is-visible');
      const overItem =
        event.target instanceof Element && event.target.closest('[data-afloat-item], [data-afloat-goat], [data-afloat-bag]');
      this.cursorEl.classList.toggle('is-over', Boolean(overItem));
      this.hovering = Boolean(overItem); // pause autoplay while reading a product (or petting the goat)
    };
    this.onPointerLeave = () => {
      this.cursorEl.classList.remove('is-visible');
      this.hovering = false;
    };
    this.addEventListener('pointermove', this.onPointerMove, { passive: true });
    // The fly-to-cart clone / cart drawer can slide between the pointer and
    // the room: that fires pointerleave (follower hides), but when they clear
    // the browser re-dispatches pointerenter WITHOUT a move — so without this
    // the room shows no pointer at all until the mouse physically moves.
    this.addEventListener('pointerenter', this.onPointerMove, { passive: true });
    this.addEventListener('pointerleave', this.onPointerLeave);

    // If the custom pointer PNG ever fails to load, the room would hide the
    // native cursor AND draw nothing — fall back to the white ring instead.
    if (this.dataset.pointer) {
      const probe = new Image();
      probe.onerror = () => this.cursorEl?.classList.remove('afloat__cursor--img');
      probe.src = this.dataset.pointer;
    }
  }

  /**
   * Drifting background motes (man.png), mirroring the homepage logo motes:
   * same float/fade animation, butterfly-ish size, scattered across the room.
   */
  setupMotes() {
    const layer = this.querySelector('[data-afloat-motes]');
    const src = this.dataset.mote;
    if (!layer || !src || reducedMotion.matches) return;

    const count = Math.max(3, Number(this.dataset.moteCount) || 4);
    for (let i = 0; i < count; i++) {
      const mote = document.createElement('img');
      mote.src = src;
      mote.alt = '';
      mote.loading = 'lazy';
      mote.decoding = 'async';
      mote.className = 'afloat__mote';
      const left = 5 + Math.random() * 88;
      let top = 10 + Math.random() * 78;
      // Keep the bottom corners clear: the goat (and the audio toggle) live
      // bottom right, the tote bottom left, and a mote over them reads as clutter.
      if ((left > 64 || left < 26) && top > 58) top = 10 + Math.random() * 46;
      mote.style.left = `${left}%`;
      mote.style.top = `${top}%`;

      const dur = 20 + Math.random() * 22;
      mote.animate(
        [
          { transform: 'translate(0, 0) rotate(0deg)', opacity: 0 },
          { opacity: 0.85, offset: 0.12 },
          {
            transform: `translate(${30 + Math.random() * 60}px, ${-40 - Math.random() * 70}px) rotate(6deg)`,
            opacity: 0.85,
            offset: 0.5,
          },
          { opacity: 0.85, offset: 0.88 },
          {
            transform: `translate(${-30 + Math.random() * 90}px, ${20 + Math.random() * 50}px) rotate(-5deg)`,
            opacity: 0,
          },
        ],
        { duration: dur * 1000, delay: -Math.random() * dur * 1000, iterations: Infinity, easing: 'ease-in-out' }
      );
      layer.appendChild(mote);
    }
  }

  /**
   * The goat grazing in the foreground. The clip is 0–3.33s grazing (a
   * palindrome, so the loop has no seam) and 3.33s+ the head-lift, the bleat
   * at MOUTH_AT and the settle back down. Idle loops the grazing half;
   * hovering/tapping cuts to the mouth-open frame with the bleat, then returns
   * to grazing. Where VP9 alpha can't render (Safari/WebKit paints it on
   * black) or motion is unwelcome, the static PNG stays and hover/tap just
   * bleats. The clip is fetched on the first idle callback, never during load,
   * so it can't compete with the product images.
   */
  setupGoat() {
    const goat = this.querySelector('[data-afloat-goat]');
    if (!goat) return;

    const soundSrc = goat.dataset.goatSound;
    const bleat = soundSrc ? new Audio(soundSrc) : null;
    if (bleat) {
      bleat.preload = 'auto';
      bleat.volume = 0.45; // ambient prop, not an alert
    }
    const playBleat = () => {
      if (!bleat || !bleat.paused) return; // one bleat at a time
      bleat.currentTime = 0;
      bleat.play().catch(() => {}); // autoplay policy may mute the very first hover
    };

    // Until the clip is in (or forever, on the fallback path) hovering the
    // static goat just bleats.
    const bleatOnly = () => playBleat();
    goat.addEventListener('pointerenter', bleatOnly);
    goat.addEventListener('click', bleatOnly);

    const videoSrc = goat.dataset.goatVideo;
    const isWebKit = /apple/i.test(navigator.vendor || '');
    if (videoSrc && !isWebKit && !reducedMotion.matches) {
      // 0–1.67s grazing forward, 1.67–3.33s the same grazing reversed (so frame
      // 3.33 == frame 0), then 3.33s+ the head-lift + bleat + settle. Every
      // wrap lands on a frame identical to the one on screen, so none show.
      const SEG_B = 3.33; // end of reversed grazing (== frame 0)
      const MOUTH_AT = 5.58; // the mouth-open frame — the sound was sampled here
      const CLIP_END = 8.3; // head is back down, hand it back to the graze loop

      const startVideo = async () => {
        // One download, two players. Two <video> elements pointed at the same
        // URL can cost two fetches, so the bytes are pulled once and both get
        // the same blob.
        let src = videoSrc;
        try {
          const response = await fetch(videoSrc, { cache: 'force-cache' });
          if (response.ok) {
            // Shopify serves .webm as `audio/webm`. A <video src="url"> ignores
            // that and sniffs the container, but a blob URL carries its type as
            // declared — so the type is restated here, or the players would
            // take a video file for an audio one and show nothing.
            const blob = new Blob([await response.arrayBuffer()], { type: 'video/webm' });
            this.goatBlobUrl = URL.createObjectURL(blob);
            src = this.goatBlobUrl;
          }
        } catch {
          // Fall back to the plain URL; the HTTP cache usually covers the second.
        }
        if (!this.isConnected) return;

        const layer = (off) => {
          const video = document.createElement('video');
          video.muted = true;
          video.playsInline = true;
          video.preload = 'auto';
          video.setAttribute('playsinline', '');
          video.setAttribute('aria-hidden', 'true');
          video.className = `afloat__goat-media afloat__goat-media--video${off ? ' is-off' : ''}`;
          video.src = src;
          goat.appendChild(video);
          return video;
        };

        // Two stacked players: one grazes, the other sits PAUSED on the
        // mouth-open frame with that frame already decoded. Hover is then a
        // class swap and a play() — no seek anywhere on the interaction path.
        // Seeking a playing video flushes the decoder, and that flush is what
        // put a beat between the hover and the head coming up.
        const graze = layer(false);
        const lift = layer(true);
        goat.removeEventListener('pointerenter', bleatOnly);
        goat.removeEventListener('click', bleatOnly);

        graze.addEventListener('playing', () => goat.querySelector('img')?.remove(), { once: true });
        graze.play().catch(() => {
          // Autoplay refused: back to the PNG, which sways and still bleats.
          graze.remove();
          lift.remove();
          goat.addEventListener('pointerenter', bleatOnly);
          goat.addEventListener('click', bleatOnly);
        });

        let parked = false;
        const park = () => {
          parked = false;
          lift.pause();
          lift.currentTime = MOUTH_AT;
        };
        lift.addEventListener('seeked', () => {
          parked = true;
        });
        if (lift.readyState >= 1) park();
        else lift.addEventListener('loadedmetadata', park, { once: true });

        let mode = 'idle'; // idle (grazing loop) → bleat (head-lift player)
        const tick = () => {
          if (!graze.isConnected) return;
          if (mode === 'idle') {
            if (graze.currentTime >= SEG_B) graze.currentTime = 0; // frame-identical wrap
          } else if (lift.currentTime >= CLIP_END || lift.ended) {
            graze.classList.remove('is-off');
            lift.classList.add('is-off');
            // Graze was rewound to frame 0 when the lift began, and the lift
            // settles on that same pose, so the handoff doesn't jump.
            graze.play().catch(() => {});
            park(); // re-arm for the next hover, unhurried and out of sight
            mode = 'idle';
          }
          requestAnimationFrame(tick);
        };
        requestAnimationFrame(tick);

        // Only used before the park has landed (the first moments after the
        // clip arrives): pair the sound to the frame that is actually
        // presented rather than to the seek, which fires when the decoder is
        // ready and so drifts with how far the frame sits from a keyframe.
        const bleatOnMouthFrame = () => {
          if (typeof lift.requestVideoFrameCallback !== 'function') {
            lift.addEventListener('seeked', playBleat, { once: true });
            return;
          }
          const waitForFrame = (_now, meta) => {
            if (meta.mediaTime >= MOUTH_AT - 0.05) {
              playBleat();
              return;
            }
            lift.requestVideoFrameCallback(waitForFrame);
          };
          lift.requestVideoFrameCallback(waitForFrame);
        };

        const trigger = () => {
          if (mode !== 'idle') return;
          mode = 'bleat';
          graze.classList.add('is-off');
          lift.classList.remove('is-off');
          graze.pause();
          // Rewind while hidden: the lift ends on frame 0's pose, and the seek
          // has the whole head-lift to land.
          graze.currentTime = 0;
          lift.play().catch(() => {});
          // The mouth frame is already decoded and on screen this compositor
          // frame, so the sound goes with it.
          if (parked) playBleat();
          else bleatOnMouthFrame();
        };
        goat.addEventListener('pointerenter', trigger);
        // Browsers keep a page silent until a click/tap/key, and a hover isn't
        // one, so the hover's bleat is often refused. A click mid-lift (the
        // hover already started it) still gets the bleat.
        goat.addEventListener('click', () => {
          if (mode === 'idle') trigger();
          else if (lift.currentTime < MOUTH_AT + 1) playBleat();
        });
      };

      // The clip is decoration and it is the heaviest thing on the page, so it
      // waits for the main thread to be done with the products rather than
      // loading alongside their images. The PNG grazes (CSS sway) until then.
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(startVideo, { timeout: 2500 });
      } else {
        setTimeout(startVideo, 800);
      }
    }
  }

  /** Category dropdown: hides products whose type doesn't match the selection. */
  setupFilter() {
    this.filterEl = this.querySelector('[data-afloat-filter]');
    if (!this.filterEl) return;
    this.onFilter = () => {
      this.clearSelection?.();
      const value = this.filterEl.value;
      this.items.forEach((item) => {
        const match = !value || item.dataset.type === value;
        item.classList.toggle('is-hidden', !match);
      });
      // Re-pack the line in the immersive room; the mobile grid reflows itself.
      if (this.classList.contains('is-ready')) {
        this.scroll = 0;
        this.target = 0;
        this.layout();
      }
    };
    this.filterEl.addEventListener('change', this.onFilter);

    // Arriving from the nav's SHOP dropdown (?category=jacket): open the room
    // already filtered. Runs before the first layout, so nothing re-packs.
    const category = new URLSearchParams(window.location.search).get('category')?.toLowerCase();
    if (category && Array.from(this.filterEl.options).some((option) => option.value === category)) {
      this.filterEl.value = category;
      this.onFilter();
    }
  }

  setupAddToCart() {
    this.adding = false;
    this.openSizes = null; // the size popover currently open, if any
    this.onClick = (event) => {
      if (!(event.target instanceof Element)) return;

      // A drag just happened — swallow the click so it doesn't open the PDP.
      if (this.dragged) {
        event.preventDefault();
        event.stopPropagation();
        this.dragged = false;
        return;
      }

      // Picking a size adds that exact variant, then dismisses the popover.
      const sizeChip = event.target.closest('[data-afloat-size]');
      if (sizeChip) {
        event.preventDefault();
        event.stopPropagation();
        // Fly + feedback from the (still visible) add button, not the chip we're
        // about to hide — a hidden element has no position to fly from.
        const addBtn = sizeChip.closest('[data-afloat-item]')?.querySelector('[data-afloat-add]');
        this.closeSizes();
        this.addToCart(sizeChip.dataset.variantId, addBtn || sizeChip, sizeChip.dataset.image);
        return;
      }

      const button = event.target.closest('[data-afloat-add]');
      if (!button) {
        this.closeSizes(); // tapping elsewhere dismisses an open picker

        // Touch: the first tap reveals the product's name + Add to cart (and
        // pauses the drift), like hover on desktop. Tapping the revealed product
        // again opens its PDP; tapping empty space clears it and resumes.
        if (this.coarse) {
          const item = event.target.closest('[data-afloat-item]');
          if (!item) {
            this.clearSelection();
            return;
          }
          if (this.activeItem !== item) {
            event.preventDefault();
            this.selectItem(item);
            return;
          }
          // Focus normally arrives on pointerdown, so this click belongs to the
          // tap that froze the product. It stops here; the next tap navigates.
          if (this.justSelected) {
            this.justSelected = false;
            event.preventDefault();
            return;
          }
        }
        return; // revealed item (or desktop): let the link navigate to its PDP
      }
      event.preventDefault();
      event.stopPropagation();

      // Products with multiple sizes reveal a Zara-style size picker first;
      // single-variant products add straight away.
      if (button.dataset.hasSizes) {
        const popover = button.parentElement.querySelector('[data-afloat-sizes]');
        this.toggleSizes(popover);
        return;
      }
      this.addToCart(button.dataset.variantId, button, button.dataset.image);
    };
    this.addEventListener('click', this.onClick);
  }

  /** Touch focus: lift one product, reveal its detail + CTA, and pause drift. */
  selectItem(item) {
    if (this.activeItem === item) return;
    this.clearSelection();
    this.activeItem = item;
    item.classList.add('is-active');
    this.selected = true; // pause the auto-drift while the user interacts
  }

  clearSelection() {
    this.justSelected = false;
    if (this.activeItem) {
      this.activeItem.classList.remove('is-active');
      this.activeItem = null;
    }
    this.closeSizes();
    this.selected = false;
  }

  /** Reveal/hide a product's size picker (only one open at a time). */
  toggleSizes(popover) {
    if (!popover) return;
    if (this.openSizes === popover) {
      this.closeSizes();
      return;
    }
    this.closeSizes();
    popover.hidden = false;
    popover.classList.add('is-open');
    popover.parentElement?.querySelector('[data-afloat-add]')?.setAttribute('aria-expanded', 'true');
    this.openSizes = popover;
  }

  closeSizes() {
    if (!this.openSizes) return;
    this.openSizes.hidden = true;
    this.openSizes.classList.remove('is-open');
    this.openSizes.parentElement?.querySelector('[data-afloat-add]')?.setAttribute('aria-expanded', 'false');
    this.openSizes = null;
  }

  /**
   * The gallery tote in the grass. Liquid renders what's in the cart on load
   * (so adds made on other pages already show); from then on every cart update
   * re-reads /cart.js and restacks the window, newest on top. Adds drop the new
   * item in and the bag takes the weight. Clicking it opens the side cart.
   */
  setupBag() {
    this.bag = this.querySelector('[data-afloat-bag]');
    if (!this.bag) return;

    this.bag.addEventListener('click', (event) => {
      event.stopPropagation(); // not a product click for the room
      if (document.querySelector('cart-drawer-component')) openCartDrawer();
      else window.location.href = window.Theme?.routes?.cart_url || '/cart';
    });

    this.onBagCartUpdate = async (event) => {
      if (event.detail?.data?.didError) return;
      const cartUrl = window.Theme?.routes?.cart_url || '/cart';
      let cart;
      try {
        const response = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } });
        if (!response.ok) return;
        cart = await response.json();
      } catch (_) {
        return;
      }
      this.fillBag(cart, event instanceof CartAddEvent);
    };
    document.addEventListener(ThemeEvents.cartUpdate, this.onBagCartUpdate);

    this.bag.addEventListener('animationend', (event) => {
      if (event.target !== this.bag) return;
      this.bag.classList.remove('is-catching');
      this.openDrawerAfterCatch?.();
    });
  }

  /** Open the side cart once the tote has caught the new item (or give up waiting). */
  holdDrawerForCatch() {
    const timer = window.setTimeout(() => this.openDrawerAfterCatch?.(), BAG_CATCH_MAX_MS);
    this.openDrawerAfterCatch = () => {
      window.clearTimeout(timer);
      this.openDrawerAfterCatch = null;
      openCartDrawer();
    };
  }

  /** Is the tote actually on screen (not hidden by a breakpoint or setting)? */
  bagIsShown() {
    return Boolean(this.bag && this.bag.getClientRects().length);
  }

  /**
   * @param {{ item_count: number, items: Array<{ image?: string, featured_image?: { url?: string } }> }} cart
   * @param {boolean} dropped a new item was just added
   */
  async fillBag(cart, dropped) {
    const stack = this.bag.querySelector('[data-afloat-bag-stack]');
    if (!stack) return;

    const count = cart.item_count || 0;
    this.bag.setAttribute('aria-label', `Open cart (${count} ${count === 1 ? 'item' : 'items'})`);

    // Newest three; data-slot sets the stacking (0 = newest, in front).
    const images = (cart.items || [])
      .slice(0, 3)
      .map((item) => item.featured_image?.url || item.image)
      .map((src) => {
        if (!src) return '';
        try {
          const url = new URL(src, window.location.origin);
          url.searchParams.set('width', String(BAG_ITEM_WIDTH));
          return url.toString();
        } catch (_) {
          return src;
        }
      });

    const next = images
      .map((src, slot) => {
        if (!src) return null;
        const img = document.createElement('img');
        img.className = 'afloat__bag-item';
        img.dataset.slot = String(slot);
        img.src = src;
        img.alt = '';
        img.width = BAG_ITEM_WIDTH;
        img.height = BAG_ITEM_WIDTH;
        return img;
      })
      .filter(Boolean);

    // Don't swap in (or drop) images that haven't loaded; a blank falls badly.
    await Promise.all(next.map((img) => img.decode().catch(() => {})));

    stack.replaceChildren(...next);
    this.bag.classList.toggle('is-filled', next.length > 0);

    const newest = next[0];
    if (!dropped || !newest || newest.dataset.slot !== '0') return;

    newest.classList.add('is-dropping');
    this.bag.classList.remove('is-catching');
    void this.bag.offsetWidth; // restart on back-to-back adds
    this.bag.classList.add('is-catching');
  }

  flyToCart(sourceEl, image) {
    // Into the tote's window when it's on screen, else up to the nav cart.
    const cartIcon = this.bagIsShown()
      ? this.bag.querySelector('[data-afloat-bag-window]')
      : document.querySelector('.header-actions__cart-icon');
    if (!cartIcon || !image || !sourceEl || reducedMotion.matches) return;

    const fly = document.createElement('fly-to-cart');
    fly.classList.add('fly-to-cart--main', 'afloat__fly');
    fly.style.setProperty('background-image', `url(${image})`);
    fly.style.setProperty('--start-opacity', '0');
    fly.source = sourceEl;
    fly.destination = cartIcon;
    document.body.appendChild(fly);
  }

  /**
   * @param {string} variantId variant to add
   * @param {HTMLElement} sourceEl element to flash feedback on / fly from
   * @param {string} image product image URL for the fly clone
   */
  async addToCart(variantId, sourceEl, image) {
    if (!variantId || sourceEl?.disabled || this.adding) return;
    this.adding = true;

    // Clicked feedback: collapse the label, swell the centred cart image.
    sourceEl?.classList.add('is-adding');
    window.setTimeout(() => sourceEl?.classList.remove('is-adding'), 900);

    this.flyToCart(sourceEl, image);

    try {
      const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
        .map((el) => el.dataset.sectionId)
        .filter(Boolean);
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sectionIds.join(','),
        }),
      });
      const data = await response.json();

      if (data?.status) return;

      sourceEl?.classList.add('is-added');
      window.setTimeout(() => sourceEl?.classList.remove('is-added'), 1200);

      document.dispatchEvent(
        new CartAddEvent(data ?? undefined, 'anemoia-collection-float', {
          source: 'product-form-component',
          itemCount: 1,
          variantId: String(variantId),
          sections: data?.sections,
        })
      );

      // Pop the side cart open on add, once the tote has caught the item.
      if (this.bagIsShown() && !reducedMotion.matches) this.holdDrawerForCatch();
      else openCartDrawer();
    } catch (error) {
      console.error('Anemoia collection add to cart failed', error);
    } finally {
      this.adding = false;
    }
  }
}

if (!customElements.get('anemoia-collection-float')) {
  customElements.define('anemoia-collection-float', AnemoiaCollectionFloat);
}
